import React, { useState, useEffect, useMemo } from "react";
import CollapsibleCard from "../common/CollapsibleCard"; 
import RouteRow from "../common/RouteRow";
import { exportRouteActivityExcel } from "../../utils/exportRouteActivityExcel";
import { exportRouteActivityPdf } from "../../utils/exportRouteActivityPdf";
import { useAuth } from "../../context/AuthContext";
import InnerSpinner from "../common/InnerSpinner";
import SectionHeader from "../common/SectionHeader";
import ButtonEnhanced from "../common/ButtonEnhanced";

// Importa tus servicios
import { getBusDailyActivity } from "../../services/reportsService";
import { getBusById } from "../../services/busService";

// Cuántos detalles de bus pedimos a la vez (la API es HTTP/2, el navegador los multiplexa)
const MAX_PARALLEL_REQUESTS = 20;

// Menos de 1 hora se muestra como "< 1h" en vez de "0h" o "0.4h"
const formatHours = (hours) => (hours < 1 ? '< 1h' : `${hours}h`);

// 🔥 SUBCOMPONENTE PARA EL HOVER
function DayStreakItem({ day, data }) {
  const [isHovered, setIsHovered] = useState(false);
  const { active, shifts, hours } = data;
  
  const borderColor = active ? '#10B981' : '#9CA3AF';
  const bgColor = active ? '#ECFDF5' : '#F3F4F6';
  const iconColor = active ? '#10B981' : '#9CA3AF';

  return (
      <div 
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', position: 'relative' }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
      >
          <div style={{ 
              width: '36px', height: '36px', borderRadius: '50%', 
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              background: bgColor,
              border: `2px solid ${active ? '#6EE7B7' : '#E5E7EB'}`,
              transition: 'all 0.2s',
              boxShadow: active ? '0 2px 4px rgba(16,185,129,0.1)' : 'none',
              cursor: 'pointer'
          }}>
              {active ? (
                  <svg style={{ transform: isHovered ? 'scale(1.15)' : 'scale(1)', transition: 'all 0.2s' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
              ) : (
                  <div style={{ width: '12px', height: '3px', backgroundColor: iconColor, borderRadius: '2px' }} />
              )}
          </div>
          <span style={{ fontSize: '12px', fontWeight: active ? 800 : 600, color: active ? '#059669' : '#9CA3AF' }}>{day}</span>
          
          {/* 🔥 TOOLTIP FLOTANTE */}
          {isHovered && (
              <div style={{
                  position: 'absolute', bottom: '100%', marginBottom: '12px', backgroundColor: 'white',
                  padding: '12px 16px', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                  pointerEvents: 'none', zIndex: 50, border: `2px solid ${borderColor}`, minWidth: '160px',
                  animation: 'fadeIn 0.2s ease-out', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', whiteSpace: 'nowrap'
              }}>
                  {active ? (
                      <>
                          <h4 style={{ margin: 0, fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 800 }}>{hours < 1 ? 'Menos de 1 hora operada' : `${hours.toFixed(1)} horas operadas`}</h4>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px', width: '100%' }}>
                              {shifts.map((shift, idx) => (
                                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F3F4F6', padding: '4px 8px', borderRadius: '6px' }}>
                                      <span style={{ fontSize: '12px' }}>⏰</span>
                                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#1F2937' }}>{shift}</span>
                                  </div>
                              ))}
                          </div>
                      </>
                  ) : (
                      <>
                          <h4 style={{ margin: 0, fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 800 }}>Estado</h4>
                          <p style={{ margin: '6px 0 0 0', fontSize: '14px', fontWeight: 700, color: '#1F2937', display: 'flex', alignItems: 'center', gap: '6px' }}>💤 Sin Actividad</p>
                      </>
                  )}
                  <div style={{ position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%) rotate(45deg)', width: '10px', height: '10px', backgroundColor: 'white', borderRight: `2px solid ${borderColor}`, borderBottom: `2px solid ${borderColor}` }} />
              </div>
          )}
      </div>
  );
}

export default function RouteActivityTab() {
  const { token } = useAuth();
  const [expandedActivityRoutes, setExpandedActivityRoutes] = useState(new Set());
  
  const [activityData, setActivityData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // --- FUNCIONES DE AYUDA BLINDADAS ---
  const getDayIndex = (dateString) => {
    const date = new Date(dateString);
    let day = date.getDay();
    return day === 0 ? 6 : day - 1; 
  };

  const formatTimeSafe = (dateString) => {
    const d = new Date(dateString);
    let h = d.getHours();
    const m = d.getMinutes().toString().padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12; 
    return `${h.toString().padStart(2, '0')}:${m} ${ampm}`;
  };

  const formatMinsToTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const period = h >= 12 ? 'PM' : 'AM';
    const fh = h % 12 === 0 ? 12 : h % 12;
    const fm = m.toString().padStart(2, '0');
    return `${fh.toString().padStart(2, '0')}:${fm} ${period}`;
  };

  const getHoursDifferenceSafe = (startStr, endStr) => {
    const s = new Date(startStr);
    const e = new Date(endStr);
    
    const startMins = (s.getHours() * 60) + s.getMinutes();
    const endMins = (e.getHours() * 60) + e.getMinutes();
    
    let diffMins = endMins - startMins;
    if (diffMins < 0) {
      diffMins += 1440; 
    }
    
    return diffMins / 60; 
  };

  // 🔥 EFECTO PRINCIPAL
  useEffect(() => {
    const loadData = async () => {
      if (!token) return;
      setIsLoading(true);

      try {
        const allActivities = await getBusDailyActivity(token);
        // Los buses sin turnos no se muestran (ni se pide su detalle)
        const rawActivities = (allActivities || []).filter(a => a.workShifts?.length > 0);
        if (rawActivities.length === 0) {
            setActivityData([]);
            return;
        }

        const uniqueBusIds = [...new Set(rawActivities.map(a => a.busId))];

        // Pedimos los detalles en paralelo (máx. MAX_PARALLEL_REQUESTS a la vez) en vez de uno por uno
        const busesDetailedInfo = {};
        const pendingBusIds = [...uniqueBusIds];
        const worker = async () => {
            while (pendingBusIds.length > 0) {
                const busId = pendingBusIds.shift();
                const busDetail = await getBusById(busId, token);
                if (busDetail) {
                    busesDetailedInfo[busId] = busDetail;
                }
            }
        };
        await Promise.all(Array.from({ length: MAX_PARALLEL_REQUESTS }, worker));

        const groupedByRoute = {};

        rawActivities.forEach(activity => {
            const busDetail = busesDetailedInfo[activity.busId];
            if (!busDetail) return;

            const superRouteInfo = busDetail.superRoute || busDetail.userSuperRoute?.superRoute || {};
            const routeInfo = busDetail.route || {};
            
            const cityInfo = superRouteInfo.city || routeInfo.city || routeInfo.startStop?.city || {};
            const cityName = cityInfo.citName || "Sin Ciudad";
            
            const routeName = routeInfo.rouName || "Sin Ruta";
            const routeId = routeInfo.rouId || 0;
            const groupKey = `${cityName}_${routeId}`;

            if (!groupedByRoute[groupKey]) {
                groupedByRoute[groupKey] = {
                    cityName: cityName,
                    routeId: routeId,
                    routeName: routeName,
                    busesMap: {} 
                };
            }

            if (!groupedByRoute[groupKey].busesMap[activity.busId]) {
                const ownerUser = busDetail.busOwner?.user || {};
                const fullName = ownerUser.userName 
                    ? `${ownerUser.userName} ${ownerUser.userLastName || ""}`.trim() 
                    : "Sin Conductor";

                groupedByRoute[groupKey].busesMap[activity.busId] = {
                    busId: busDetail.busName || activity.busId,
                    plate: busDetail.busRegistrationId || "Sin Placa",
                    driver: fullName,
                    totalHours: 0,
                    earliestMin: 1440, 
                    latestMin: 0,
                    streak: Array.from({ length: 7 }, () => ({ active: false, hours: 0, shifts: [] }))
                };
            }

            const currentBusEntry = groupedByRoute[groupKey].busesMap[activity.busId];

            // PROCESAMOS LOS TURNOS
            activity.workShifts.forEach(shift => {
                const shiftStr = `${formatTimeSafe(shift.startTime)} - ${formatTimeSafe(shift.endTime)}`;
                const startObj = new Date(shift.startTime);
                const endObj = new Date(shift.endTime);
                const dayIdx = getDayIndex(shift.startTime);

                if (dayIdx >= 0 && dayIdx <= 6) {
                    const dayData = currentBusEntry.streak[dayIdx];
                    
                    if (!dayData.shifts.includes(shiftStr)) {
                        dayData.shifts.push(shiftStr);
                        dayData.active = true;
                        
                        const shiftHours = getHoursDifferenceSafe(shift.startTime, shift.endTime);
                        dayData.hours += shiftHours;
                        currentBusEntry.totalHours += shiftHours;

                        const startMins = startObj.getHours() * 60 + startObj.getMinutes();
                        const endMins = endObj.getHours() * 60 + endObj.getMinutes();
                        
                        if (startMins < currentBusEntry.earliestMin) currentBusEntry.earliestMin = startMins;
                        if (endMins > currentBusEntry.latestMin) currentBusEntry.latestMin = endMins;
                    }
                }
            });
        });

        // Convertimos los diccionarios a los Arreglos Finales
        // (los buses "Sin Horario" y las rutas que se quedan vacías no se muestran)
        const finalArray = Object.values(groupedByRoute).map(group => {
            const busesArray = Object.values(group.busesMap)
                .filter(b => b.earliestMin < 1440)
                .map(b => ({
                    ...b,
                    totalHours: parseFloat(b.totalHours.toFixed(1)),
                    streak: b.streak.map(day => ({
                        ...day,
                        hours: parseFloat(day.hours.toFixed(1))
                    })),
                    avgSchedule: `${formatMinsToTime(b.earliestMin)} - ${formatMinsToTime(b.latestMin)}`
                }));

            return {
                cityName: group.cityName,
                routeId: group.routeId,
                routeName: group.routeName,
                buses: busesArray
            };
        }).filter(route => route.buses.length > 0);

        setActivityData(finalArray);

      } catch (error) {
        console.error("Error cargando la actividad de rutas:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [token]);

  const groupedActivityData = useMemo(() => {
    return activityData.reduce((groups, route) => {
        if (!groups[route.cityName]) groups[route.cityName] = [];
        groups[route.cityName].push(route);
        return groups;
    }, {});
  }, [activityData]);

  const toggleActivityRouteExpansion = (routeId) => {
      setExpandedActivityRoutes(prev => {
          const next = new Set(prev);
          if (next.has(routeId)) next.delete(routeId);
          else next.add(routeId);
          return next;
      });
  };

  const handleExportExcel = () => exportRouteActivityExcel(activityData);
  const handleExportPDF = () => exportRouteActivityPdf(activityData);

  // 🔥 VALIDACIÓN PARA BLOQUEAR LOS BOTONES DE EXPORTACIÓN
  const isExportDisabled = isLoading || activityData.length === 0;

  return (
      <div style={{ flex: 1, backgroundColor: '#FFFFFF', overflowY: 'auto' }}>
          <div style={{ padding: '32px 24px', maxWidth: '1100px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
              <SectionHeader
                  icon={
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                  }
                  title="Actividad de Rutas (Semanal)"
                  subtitle="Monitorea el trabajo, horarios y estadísticas operativas de cada autobús, organizado por ciudad."
              />

              <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                      <ButtonEnhanced
                          variant="green"
                          onClick={handleExportExcel}
                          disabled={isExportDisabled}
                          style={{ flex: 1, padding: '12px 16px', fontWeight: 'bold' }}
                          icon={
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                              </svg>
                          }
                      >
                          Descargar Excel
                      </ButtonEnhanced>

                      <ButtonEnhanced
                          variant="red"
                          onClick={handleExportPDF}
                          disabled={isExportDisabled}
                          style={{ flex: 1, padding: '12px 16px', fontWeight: 'bold' }}
                          icon={
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                              </svg>
                          }
                      >
                          Descargar PDF
                      </ButtonEnhanced>
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #E5E7EB', margin: '24px 0 0 0' }} />
              </div>

              {isLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                      <InnerSpinner type="threeDots" />
                  </div>
              ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {Object.entries(groupedActivityData).map(([cityName, routes], cityIdx) => (
                          <CollapsibleCard key={cityIdx} title={cityName} isActive={true} showToggleBtn={false} defaultExpanded={true} titleSize={18}>
                              <div style={{ display: 'flex', flexDirection: 'column', padding: '12px 0' }}>
                                  {routes.map((route, idx) => {
                                      const isExpanded = expandedActivityRoutes.has(route.routeId);
                                      return (
                                          <div key={idx}>
                                              <RouteRow 
                                                  name={route.routeName} 
                                                  countInfo={route.buses.length > 0 ? route.buses.length : 1} 
                                                  isVisible={true} 
                                                  isExpanded={isExpanded} 
                                                  onExpandToggle={() => toggleActivityRouteExpansion(route.routeId)}
                                                  hasChildren={route.buses.length > 0} 
                                                  showToggle={false} 
                                                  textSize={15}
                                                  lengthSize={13}    
                                              />

                                              {isExpanded && route.buses.length > 0 && (
                                                  <div style={{ padding: '12px 16px', backgroundColor: '#FAFAFA', borderBottom: '1px dashed #E5E7EB' }}>
                                                      {route.buses.map((bus, busIdx) => (
                                                          <div key={busIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: busIdx < route.buses.length - 1 ? '12px' : '0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                              <div style={{ flex: 1, minWidth: '200px' }}>
                                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                                      <span style={{ fontSize: '16px', fontWeight: 800, color: '#1F2937' }}>Autobús {bus.busId}</span>
                                                                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#D97706', background: '#FEF3C7', padding: '2px 8px', borderRadius: '6px' }}>{bus.plate}</span>
                                                                  </div>
                                                                  <div style={{ fontSize: '13px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}>
                                                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                                                                      {bus.driver}
                                                                  </div>
                                                              </div>
                                                              
                                                              {/* CIRCULITOS VERDES/GRISES POR DÍA */}
                                                              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flex: 1.5 }}>
                                                                  {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, dayIdx) => <DayStreakItem key={dayIdx} day={day} data={bus.streak[dayIdx]} />)}
                                                              </div>
                                                              
                                                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                                                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                                      <span style={{ fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 800 }}>Horas Trabajadas (Semana)</span>
                                                                      <span style={{ fontSize: '20px', fontWeight: 900, color: '#10B981' }}>{formatHours(bus.totalHours)}</span>
                                                                  </div>
                                                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                                      <span style={{ fontSize: '11px', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 800 }}>Horario Promedio</span>
                                                                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                                                          {bus.avgSchedule}
                                                                      </span>
                                                                  </div>
                                                              </div>
                                                          </div>
                                                      ))}
                                                  </div>
                                              )}
                                          </div>
                                      );
                                  })}
                              </div>
                          </CollapsibleCard>
                      ))}

                      {Object.keys(groupedActivityData).length === 0 && (
                          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                              <p>No hay actividad registrada en la plataforma.</p>
                          </div>
                      )}
                  </div>
              )}
          </div>
      </div>
  );
}